import React, { useRef, forwardRef, useImperativeHandle, useState } from "react";
import { HotTable } from "@handsontable/react";
import { utils } from "../../../utils";
import * as run from "../redux";
import swal from "sweetalert";
import { service as activityService } from "~/activity/redux";
import { ClipLoader } from "react-spinners";

import "./ssot_item_table.component.css";

const DATE_FORMAT = "DD/MM/YYYY";
const TABLE_HEIGHT = 400;
const COL_HEADERS = [
    "Activity",
    "SMP Name",
    "Material item Number",
    "Item Description",
    "Qty",
    "Site Id / Name (as in IPM)",
    "Customer request delivery date",
    "Project Request delivery date",
    "Subcontractor / Supplier",
    "Synergy or Non-Synergy",
    "SAP WBS Region Description",
    "SAP WBS Zone Description",
    "Site WBS element",
    "PO Release Instructions",
    "WP ID (Work Package ID)",
    "SOG",
    "VOA cases"
];

/**
 * Component that renders a Handsontable for editing SSOT items.
 *
 * Features:
 * - Displays read-only columns for activity, material, site, etc.
 * - Editable columns for Subcontractor/Supplier, Synergy, and PO Release Instructions with autocomplete.
 * - **Smart Autocomplete**: Implements caching for vendor and PO release instruction searches to reduce API calls.
 * - **Handsontable Integration**: Handles "Autofill" operations safely by bypassing async fetchers to prevent race conditions.
 * - **Data Normalization**: Automatically converts all date fields to "DD/MM/YYYY" format before submission.
 * - **Validation**: Validates required fields before allowing submission.
 * - Exposed method `getData` to retrieve the current table data via ref.
 *
 * @param {Object} props - The component props.
 * @param {Array<Object>} props.data - The data to display in the table.
 * @param {Function} props.onSubmit - Callback function triggered when the submit button is clicked. Receives the normalized table data.
 * @param {Function} props.onSubmittingStateChange - Callback to notify parent about submitting state changes (true/false).
 * @param {React.Ref<Object>} ref - Ref to access the `getData` method.
 */
const SSOTItemTable = forwardRef(({ data, onSubmit, onSubmittingStateChange }, ref) => {
    const hotTableComponent = useRef(null);
    const isTableEmpty = !(data && data.length > 0);
    const isSubmittingRef = useRef(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isAutofillRef = useRef(false);
    const vendorCacheRef = useRef(new Map());
    const poReleaseCacheRef = useRef(new Map());
    const snergyCacheRef = useRef(new Map());
    const sogCacheRef = useRef(new Map());

    /**
     * Exposes imperative methods to the parent component via ref:
     * - `getData()` — returns the current Handsontable source data (all rows as objects).
     * - `clearFilters()` — removes any active column filters from the table.
     */
    useImperativeHandle(ref, () => ({
        getData: () => {
            if (hotTableComponent.current) {
                return hotTableComponent.current.hotInstance.getSourceData();
            }
            return [];
        },
        clearFilters: () => {
            if (hotTableComponent.current) {
                const hot = hotTableComponent.current.hotInstance;
                const filtersPlugin = hot.getPlugin('filters');
                if (filtersPlugin) {
                    filtersPlugin.clearConditions();
                    filtersPlugin.filter();
                }
            }
        }
    }));

    /**
     * Custom Handsontable cell renderer for date columns.
     * Formats the value to DD/MM/YYYY using `utils.dateToStr` and applies
     * read-only dimmed styling (`htDimmed`).
     *
     * @param {Object}          instance        - Handsontable instance (unused).
     * @param {HTMLElement}     td              - Table cell DOM element to render into.
     * @param {number}          row             - Row index (unused).
     * @param {number}          col             - Column index (unused).
     * @param {string}          prop            - Column data property name (unused).
     * @param {Date|string|null} value          - Cell value to format.
     * @param {Object}          _cellProperties - Cell properties (unused).
     */
    const dateRenderer = (instance, td, row, col, prop, value, _cellProperties) => {
        if (value) {
            td.innerHTML = utils.dateToStr(value, DATE_FORMAT);
        } else {
            td.innerHTML = "";
        }
        td.className = "htCenter htMiddle htDimmed";
    };

    const getColHeaders = () => COL_HEADERS;

    /**
     * Reusable Handsontable cell validator for required (non-empty) fields.
     * Marks the cell invalid (highlighted in red) when the value is blank or whitespace-only.
     *
     * @param {*}        value    - The cell value to validate.
     * @param {Function} callback - Must be called with `true` (valid) or `false` (invalid).
     */
    const requiredFieldValidator = (value, callback) => {
        if (!value || String(value).trim() === "") {
            callback(false);
        } else {
            callback(true);
        }
    };

    /**
     * Returns the Handsontable column configuration array.
     * Editable columns (`subcontractor_supplier`, `synergy`, `po_related_instruction`, `sog`)
     * use autocomplete / dropdown sources backed by smart in-memory caches.
     *
     * @returns {Array<Object>} Array of Handsontable column descriptor objects.
     */
    const getColumns = () => {
        /**
         * Returns a Handsontable-compatible autocomplete `source` function backed by
         * an in-memory Map cache. During an "autofill" drag operation it bypasses the
         * async fetcher and returns cached results immediately, preventing race conditions
         * with the async cell editor.
         *
         * @param {React.MutableRefObject<Map>} cacheRef - Ref holding query→results cache.
         * @param {Function} fetcher - Async function `(query: string) => Promise<string[]>`.
         * @returns {Function} Autocomplete source: `(query: string, process: Function) => void`.
         */
        const safeAutocompleteSource = (cacheRef, fetcher) => (query, process) => {
            const q = String(query || "").trim();
            const cacheKey = q || "__initial__";
            
            if (isAutofillRef.current) {
                process(cacheRef.current.get(cacheKey) || []);
                return;
            }

            if (cacheRef.current.has(cacheKey)) {
                process(cacheRef.current.get(cacheKey));
                return;
            }
            
            fetcher(q)
                .then(resp => {
                    cacheRef.current.set(cacheKey, resp || []);
                    process(resp || []);
                })
                .catch(() => process([]));
        };

        return [
            { data: "activity_name", readOnly: true, width: 200, wordWrap: true },
            { data: "smp_name", readOnly: true },
            { data: "material_number_item", readOnly: true },
            { data: "item_description", readOnly: true },
            {
                data: "quantity",
                type: "numeric",
                numericFormat: {
                    pattern: "0,0.00"
                },
                readOnly: true
            },
            { data: "site_id_name", readOnly: true },
            { data: "customer_request_delivery_date", renderer: dateRenderer, readOnly: true },
            { data: "project_request_delivery_date", renderer: dateRenderer, readOnly: true },
            {
                data: "subcontractor_supplier",
                type: "autocomplete",
                source: safeAutocompleteSource(vendorCacheRef, q => run.service.vendorAutocomplete(q)),
                strict: false,
                trimDropdown: false,
                allowInvalid: true,
                allowEmpty: false,
                validator: requiredFieldValidator,
                readOnly: isTableEmpty || isSubmitting
            },
            {
                data: "synergy",
                type: "dropdown",
                source: safeAutocompleteSource(snergyCacheRef, q =>
                    activityService.synergyAutocomplete(q)
                ),
                allowEmpty: false,
                readOnly: isTableEmpty || isSubmitting
            },
            { data: "sap_wbs_region_description", readOnly: true },
            { data: "sap_wbs_zone_description", readOnly: true },
            { data: "sap_wbs_element", readOnly: true },
            {
                data: "po_related_instruction",
                type: "autocomplete",
                source: safeAutocompleteSource(poReleaseCacheRef, q =>
                    activityService.poReleaseInstructionsAutocomplete(q)
                ),
                strict: false,
                allowInvalid: true,
                allowEmpty: false,
                validator: requiredFieldValidator,
                readOnly: isTableEmpty || isSubmitting
            },
            { data: "wp_id", readOnly: true },
            {
                data: "sog",
                type: "autocomplete",
                source: safeAutocompleteSource(sogCacheRef, q => run.service.sogAutocomplete(q)),
                strict: false,
                allowInvalid: true,
                allowEmpty: false,
                validator: requiredFieldValidator,
                readOnly: isTableEmpty || isSubmitting
            },
            { data: "voa_case_name", readOnly: true }
        ];
    };

    const DISABLED_FILTERS = [0, 1, 4, 16]
    
    const hidden = function ()   {
        const col = this.getSelectedRangeLast().to.col;
        return DISABLED_FILTERS.includes(col);
    }
    
    const table_op = {
        width: "100%",
        height: TABLE_HEIGHT,
        stretchH: "all",
        beforeChange: (changes) => {
            if (!changes) return;
            changes.forEach(change => {
                const [, prop, , newVal] = change;
                if (prop === "subcontractor_supplier" && typeof newVal === "string") {
                    const separatorIndex = newVal.indexOf(" - ");
                    if (separatorIndex > -1) {
                        const vendorNumber = newVal.substring(0, separatorIndex);
                        if (vendorNumber) {
                            change[3] = vendorNumber;
                        }
                    }
                }
            });
        },
        afterChange: (changes, source) => {
            if (source && String(source).toLowerCase().includes("autofill")) {
                isAutofillRef.current = true;
                setTimeout(() => {
                    isAutofillRef.current = false;
                }, 0);
            }
        },
        beforeKeyDown: (_e) => {
            if (isAutofillRef.current && hotTableComponent.current) {
                const editor = hotTableComponent.current.hotInstance.getActiveEditor();
                editor && editor.close && editor.close();
            }
        },
        dropdownMenu: {
            items: {
                filter_by_condition: {
                    hidden
                },
                filter_by_value: {
                    hidden
                },
                filter_action_bar: {
                    hidden
                }
            }
        },
        afterGetColHeader(col, TH) {
            if (DISABLED_FILTERS.includes(col)) {
                const button = TH.querySelector(".changeType");
                if (button) {
                    button.parentElement.removeChild(button);
                }
            }
        },
        settings: {
            tableClassName: ["table", "table-bordered", "table-striped"],
            wordWrap: true,
            stretchH: "none",
            className: "htCenter htMiddle",
            colHeaders: getColHeaders(),
            columns: getColumns(),
            // When using fillHandle (drag-to-fill), Handsontable can sometimes trigger unwanted value changes or weird focus issues with async editors.
            // We use `isAutofillRef` in `afterChange` and `beforeKeyDown` hooks to mitigate conflicts.
            fillHandle: {
                direction: "vertical",
                autoInsertRow: false
            },
            data: data && data.length > 0 ? data : [{}],
            filters: true,
            columnSorting: {
                initialConfig: {
                    column: 5, // site_id_name
                    sortOrder: "asc"
                }
            }
        }
    };
    
    /**
     * Handles the "Send SSOT Items" button click.
     *
     * 1. Clears active column filters so every row is included in validation.
     * 2. Closes the active cell editor to flush any pending edit.
     * 3. Runs Handsontable's built-in cell validation on all rows.
     * 4. On success: normalises date fields to DD/MM/YYYY and calls `onSubmit`.
     * 5. On failure: shows a SweetAlert error and keeps the table editable.
     *
     * Uses `isSubmittingRef` as a synchronous guard to prevent double-submission.
     */
    const handleSubmit = () => {
        if (!hotTableComponent.current || isSubmittingRef.current) {
            return;
        }
        
        const filtersPlugin = hotTableComponent.current.hotInstance.getPlugin("filters");
        if (filtersPlugin) {
            filtersPlugin.clearConditions();
            filtersPlugin.filter();
        }
        
        isSubmittingRef.current = true;
        try {
            const editor = hotTableComponent.current.hotInstance.getActiveEditor();
            if (editor && typeof editor.close === "function") {
                editor.close();
            }
        } catch (_e) {
            // ignore
        }

        hotTableComponent.current.hotInstance.validateCells(valid => {
            if (valid) {
                setIsSubmitting(true);
                if (onSubmittingStateChange) onSubmittingStateChange(true);

                const tableData = hotTableComponent.current.hotInstance.getSourceData();
                const columns = getColumns();
                const dataToSubmit = tableData.map(row => {
                    const newRow = { ...row };
                    columns.forEach(col => {
                        if (newRow[col.data] === undefined) {
                            newRow[col.data] = null;
                        }
                    });
                    const date_columns = ["customer_request_delivery_date", "project_request_delivery_date"]
                    date_columns.forEach(field => {
                        const v = newRow[field];
                        if (!v) {
                            newRow[field] = null;
                            return;
                        }

                        // Handsontable might return a JS Date or a String depending on interactions.
                        // We normalize everything to DD/MM/YYYY for the backend.
                        if (v instanceof Date) {
                            newRow[field] = utils.dateToStr(v, DATE_FORMAT);
                            return;
                        }

                        if (typeof v === "string") {
                            const parsed = utils.strToDateTime(v) || utils.strToDate(v);
                            newRow[field] = parsed ? utils.dateToStr(parsed, DATE_FORMAT) : v;
                        }
                    });

                    return newRow;
                });

                const finish = () => {
                    isSubmittingRef.current = false;
                    setIsSubmitting(false);
                    if (onSubmittingStateChange) onSubmittingStateChange(false);
                    try {
                        hotTableComponent.current.hotInstance.render();
                    } catch (_e) {
                        // ignore
                    }
                };

                try {
                    const maybePromise = onSubmit && onSubmit(dataToSubmit);
                    Promise.resolve(maybePromise).finally(finish);
                } catch (e) {
                    finish();
                }
            } else {
                swal({
                    title: "Validation Error",
                    text: "Please fill in all required fields (marked in red).",
                    icon: "warning"
                });
                isSubmittingRef.current = false;

                try {
                    hotTableComponent.current.hotInstance.render();
                } catch (_e) {
                    // ignore
                }
            }
        });
    };

    return (
        <div>
            <div style={{ marginBottom: "10px", textAlign: "right" }}>
                <button className="btn btn-primary btn-sm" onClick={handleSubmit} disabled={isTableEmpty || isSubmitting}>
                    {isSubmitting && (
                        <span style={{ display: "inline-flex", alignItems: "center", marginRight: 8 }}>
                            <ClipLoader size={14} color="#fff" />
                        </span>
                    )}
                    {isSubmitting ? "Sending..." : "Send SSOT Items"}
                </button>
            </div>
            <HotTable ref={hotTableComponent} {...table_op} />
        </div>
    );
});

export default SSOTItemTable;

